/**
 * @file Reconciliation and Failed Action on core-service's `failed-integration-actions` routes: the
 * grammar query each screen sends, the page loop that reaches every row, the admin check the write
 * routes apply, and the plain words for every answer. Merchant **pkid** everywhere.
 */
import endpoints from '@/utils/endpoints.js'
import { cfAxios } from '@/utils/init-axios.js'
import {
  RETRY_ALL_BACKGROUND,
  RETRY_QUEUED_TOAST,
  isRetryQueued
} from '@/utils/failedActionRetry'

export type FailedActionKind = 'post_payment' | 'other'

/** The route's `maxLimit`; a larger `limit` is clamped to it. */
export const PAGE_LIMIT = 1000
/** The route's `maxPage`. */
export const MAX_PAGES = 1000

export const ADMIN_ONLY_MESSAGE =
  'Only an admin can retry or clear transactions.'
export const NOT_INTEGRATED_MESSAGE = 'This merchant has no DMS integration.'
const LIST_FAILED_MESSAGE = 'Unable to load transactions. Try again.'

export interface FailedActionFilters {
  search?: string
  statuses?: string[]
}

export interface FailedActionRow {
  pkid: number
  id: string
  [key: string]: unknown
}

interface FailedActionPage {
  count: number
  capped: boolean
  results: FailedActionRow[]
}

export interface RetryAnswer {
  status: 'queued' | 'skipped'
  reason?: string
  paymentStatus?: string
}

export interface RetryAllCounts {
  queued: number
  skipped: number
  failed: number
  inFlight: number
  remaining: number
  skippedReasons?: Record<string, number>
}

/**
 * One page's query, in grammar names only. No date and no `sort`: the pages have no date control,
 * and the route's default order is the one they want.
 * @param kind which screen's rows
 * @param filters the page's search box and statuses
 * @param page 1-based page number
 * @returns the axios `params`
 */
export function listParams(
  kind: FailedActionKind,
  filters: FailedActionFilters,
  page: number
): Record<string, string | number> {
  const { search, statuses } = filters
  const term = (search ?? '').trim()

  return {
    kind__exact: kind,
    status__in: (statuses?.length ? statuses : ['unposted']).join(','),
    ...(term ? { search: term } : {}),
    page,
    limit: PAGE_LIMIT
  }
}

/**
 * Every matching row, page by page, until `count` rows arrive or a page comes back short.
 * @param merchantPkid the merchant pkid
 * @param kind which screen's rows
 * @param filters the page's search box and statuses
 * @returns the rows, and whether the route capped its count
 * @throws the request's own error
 */
export async function loadAllFailedActions(
  merchantPkid: number | string,
  kind: FailedActionKind,
  filters: FailedActionFilters
): Promise<{ rows: FailedActionRow[]; capped: boolean }> {
  const rows: FailedActionRow[] = []
  let capped = false
  for (let page = 1; page <= MAX_PAGES; page++) {
    const { data } = await cfAxios.get<FailedActionPage>(
      endpoints.failedIntegrationActions(merchantPkid),
      { params: listParams(kind, filters, page) }
    )
    const results = data.results ?? []
    rows.push(...results)
    capped = Boolean(data.capped)
    if (results.length < PAGE_LIMIT || rows.length >= data.count) break
  }

  return { rows, capped }
}

/**
 * Core's write routes admit role `admin` or `super_admin`, or a super tenant (`assertMerchantAdmin`).
 * @param accessClaims the session's access claims, carrying `roles`
 * @param firebaseClaims the gateway token's claims, carrying `tenantType`
 * @returns whether the write buttons may show
 */
export function canWriteFailedActions(
  accessClaims?: { roles?: string[] } | null,
  firebaseClaims?: { tenantType?: string } | null
): boolean {
  const roles = accessClaims?.roles ?? []

  return (
    roles.includes('admin') ||
    roles.includes('super_admin') ||
    firebaseClaims?.tenantType === 'super'
  )
}

const PAYMENT_STATUS_TEXT: Record<string, string> = {
  VOID: 'the payment was voided',
  REFUND: 'the payment was refunded',
  FAILED: 'the payment failed',
  PENDING: 'the payment has not settled yet',
  ACH_RETURN: 'the bank returned the payment',
  NEEDS_REVIEW: 'the payment is under review',
  CANCELLED: 'the payment was cancelled',
  CHARGEBACK: 'the payment was charged back'
}

// `gone` and `already_resolved` come only from Retry All; the rest are payment-service's skip reasons.
const REASON_TEXT: Record<string, string> = {
  already_posted: 'your DMS already has this payment',
  not_found: 'the payment could not be found',
  other_merchant: 'the payment belongs to another merchant',
  no_integration: 'this merchant has no DMS integration',
  gone: 'it was cleared or replaced meanwhile',
  already_resolved: 'it is already resolved'
}

/**
 * @param reason the skip reason code
 * @param paymentStatus `payment_payment.status`, sent with `not_paid`
 * @returns why the payment was not reposted, in plain words
 */
export function skipReasonText(reason?: string, paymentStatus?: string) {
  if (reason === 'not_paid') {
    return (
      PAYMENT_STATUS_TEXT[paymentStatus ?? ''] ??
      `the payment is not paid (${paymentStatus})`
    )
  }

  return REASON_TEXT[reason ?? ''] ?? `it could not be reposted (${reason})`
}

/**
 * @param answer R2's 200 body
 * @returns the outcome and the toast for it
 */
export function retryAnswerMessage(answer: RetryAnswer): {
  status: 'queued' | 'skipped'
  message: string
} {
  if (isRetryQueued(answer)) {
    return { status: 'queued', message: RETRY_QUEUED_TOAST }
  }

  return {
    status: 'skipped',
    message: `Not reposted: ${skipReasonText(answer?.reason, answer?.paymentStatus)}.`
  }
}

function errorParts(error: unknown): {
  status?: number
  data: Record<string, unknown>
} {
  const response = (error as { response?: { status?: number; data?: unknown } })
    ?.response
  const data = response?.data

  return {
    status: response?.status,
    data:
      data && typeof data === 'object' ? (data as Record<string, unknown>) : {}
  }
}

/**
 * A failed Retry or Clear in plain words. `refresh` says the list on screen is stale.
 * @param error the rejected request's error
 * @param fallback the caller's message for any other failure
 * @param formatTime renders `retryableAt` in the user's local date and time; only Retry answers it
 * @returns the toast text, and whether to reload the list
 */
export function writeErrorMessage(
  error: unknown,
  fallback: string,
  formatTime: (iso: string) => string = iso => new Date(iso).toLocaleString()
): { message: string; refresh: boolean } {
  const { status, data } = errorParts(error)
  const code = data.errorCode

  if (status === 409 && code === 'RETRY_IN_FLIGHT') {
    const when =
      typeof data.retryableAt === 'string' && data.retryableAt
        ? `after ${formatTime(data.retryableAt)}`
        : 'later'

    return {
      message: `A retry is already in progress. Try again ${when}.`,
      refresh: false
    }
  }
  if (code === 'ALREADY_RESOLVED') {
    return { message: 'This transaction is already resolved.', refresh: true }
  }
  if (code === 'NOT_RETRYABLE') {
    return { message: 'Only a payment post can be retried.', refresh: false }
  }
  if (status === 404) {
    return { message: 'This transaction no longer exists.', refresh: true }
  }
  if (status === 403) {
    return { message: ADMIN_ONLY_MESSAGE, refresh: false }
  }

  return { message: fallback, refresh: false }
}

/**
 * Retry All's counts, one line each, zero counts left out.
 * @param counts R3's 200 body
 * @returns the lines to show
 */
export function retryAllSummary(counts: RetryAllCounts): string[] {
  const lines: string[] = []
  if (counts.queued > 0) {
    lines.push(`${counts.queued} queued. ${RETRY_ALL_BACKGROUND}`)
  }
  if (counts.skipped > 0) {
    const reasons = Object.entries(counts.skippedReasons ?? {}).map(
      ([reason, n]) =>
        `${n} ${reason === 'not_paid' ? 'not paid (voided, refunded or failed)' : skipReasonText(reason)}`
    )
    lines.push(
      reasons.length
        ? `${counts.skipped} not reposted: ${reasons.join(', ')}.`
        : `${counts.skipped} not reposted.`
    )
  }
  if (counts.inFlight > 0) {
    lines.push(`${counts.inFlight} already in progress; try again later.`)
  }
  if (counts.failed > 0) {
    lines.push(`${counts.failed} could not be sent. Try again later.`)
  }
  if (counts.remaining > 0) {
    lines.push(
      `${counts.remaining} not reached yet. Press Retry All again to continue.`
    )
  }

  return lines.length ? lines : ['Nothing to repost.']
}

/**
 * Clear All for one screen's kind. Never rejects: a failure comes back as its message.
 * @param merchantPkid the merchant pkid
 * @param kind which screen's rows
 * @returns whether it cleared, and the toast for the outcome
 */
export async function clearAllFailedActions(
  merchantPkid: number | string,
  kind: FailedActionKind
): Promise<{ ok: boolean; message: string }> {
  try {
    const { data } = await cfAxios.post<{ cleared?: number }>(
      endpoints.failedIntegrationActionsClearAll(merchantPkid),
      { kind }
    )

    return { ok: true, message: clearedMessage(data?.cleared ?? 0) }
  } catch (error) {
    return {
      ok: false,
      message: writeErrorMessage(
        error,
        'Unable to Resolve all this time, try again later.'
      ).message
    }
  }
}

/**
 * @param cleared R5's `cleared` count
 * @returns Clear All's toast
 */
export function clearedMessage(cleared: number): string {
  if (!cleared) return 'Nothing to clear.'

  return `Cleared ${cleared} transaction${cleared === 1 ? '' : 's'}.`
}

/**
 * @param error the rejected list request's error
 * @returns the text the screen shows instead of rows
 */
export function listErrorMessage(error: unknown): string {
  const { status, data } = errorParts(error)

  return status === 403 && data.errorCode === 'MERCHANT_NOT_INTEGRATED'
    ? NOT_INTEGRATED_MESSAGE
    : LIST_FAILED_MESSAGE
}
